'use client';

import { datadogRum } from '@datadog/browser-rum';
import { useEffect } from 'react';
import {
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_SERVICE,
} from '@/core/constants/datadog';
import { APP_VERSION } from '@/core/constants/env';
import pathnameWithoutLocale from '@/core/utils/pathname-without-locale';
import scrubSensitiveParams from '@/core/utils/scrub-sensitive-params';

type DatadogInitProps = {
  environment: string | null;
};

export default function DatadogInit({ environment }: DatadogInitProps) {
  useEffect(() => {
    // Strict Mode remounts effects in development; RUM only accepts one init.
    if (datadogRum.getInitConfiguration()) return;

    datadogRum.init({
      applicationId: DATADOG_APPLICATION_ID,
      clientToken: DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.eu',
      service: DATADOG_SERVICE,
      // Must match `--release-version` on `datadog-ci sourcemaps upload`.
      version: APP_VERSION || undefined,
      env: environment,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackBfcacheViews: true,
      defaultPrivacyLevel: 'mask-user-input',

      allowedTracingUrls: [
        {
          match: (url: string) => url.includes('.mindoasis.com'),
          propagatorTypes: ['tracecontext', 'datadog'],
        },
      ],
      trackResources: true,

      // Tag every RUM event with a locale-independent page_type so the purchase
      // funnel (@context.page_type) works across all markets. View names are
      // locale-prefixed (/de-de/cart, /it-it/cart …) and Datadog funnel steps
      // match a facet value exactly, so a raw view name can't span locales.
      // The cart/checkout/orders route segments are NOT localized, so keying off
      // the first path segment after the locale is reliable.
      beforeSend: event => {
        if (event.view) {
          /* eslint-disable no-param-reassign -- RUM SDK API */
          event.view.url = scrubSensitiveParams(event.view.url);
          event.view.referrer = event.view.referrer
            ? scrubSensitiveParams(event.view.referrer)
            : event.view.referrer;
          /* eslint-enable no-param-reassign */
        }

        try {
          const url = event.view?.url;
          if (url) {
            const { pathname, searchParams } = new URL(url);
            const seg =
              pathnameWithoutLocale(pathname).split('/').filter(Boolean)[0] ??
              '';
            let pageType = seg || 'home';

            if (
              searchParams.get('success') === 'true' &&
              (seg === 'checkout' || seg === 'orders')
            ) {
              pageType = 'order_success';
            } else if (seg === 'orders') {
              pageType = 'order_view';
            }
            // Datadog beforeSend is expected to mutate the event in place.
            // eslint-disable-next-line no-param-reassign -- RUM SDK API
            event.context = { ...event.context, page_type: pageType };
          }
        } catch {
          // never let instrumentation break event submission
        }
        return true;
      },
    });
  }, [environment]);

  // Render nothing - this component is only included so that the init code
  // above will run client-side
  return null;
}
